/**
 * Determines whether a browser notification should be shown for a completed stream.
 * Returns a simple boolean decision based on visibility and context.
 */
export function shouldShowBrowserNotification({
  isChatVisible,
  isStandaloneMode,
  isCurrentChat,
  isOrphanedStream,
}: {
  isChatVisible: boolean;
  isStandaloneMode: boolean;
  isCurrentChat: boolean;
  isOrphanedStream: boolean;
}): boolean {
  // Always show notification for orphaned streams (user likely doesn't have that chat open)
  if (isOrphanedStream) {
    return true;
  }

  // If chat is minimized (not visible), always show notification regardless of whether it's current
  // The user can't see it, so they need a notification
  if (!isChatVisible) {
    return true;
  }

  // Don't show notification if this is the currently active AND visible chat (user can see it directly)
  if (isCurrentChat && isChatVisible) {
    return false;
  }

  if (!isCurrentChat && isChatVisible) {
    return true;
  }

  // In standalone mode, show notification if chat was visible when streaming started
  // This handles the case where user switches chats mid-stream
  if (isStandaloneMode && isChatVisible) {
    return true;
  }

  return false;
}

/**
 * Checks if the browser tab and window are currently visible/active.
 * Returns true only if both the tab is visible AND the browser window has focus.
 */
export function isTabAndWindowVisible(): boolean {
  const isTabVisible = document.visibilityState === 'visible';
  const isWindowFocused = document.hasFocus();
  return isTabVisible && isWindowFocused;
}
